import {
  COVER_ROLE,
  clearEntityImage,
  listEntityImages,
  setEntityImage,
} from '@repo/db/queries/assets'
import { effectiveStock, peakHeldQuantity } from '@repo/db/queries/availability'
import { listFutureHolds } from '@repo/db/queries/bookings'
import {
  createOfferingCategory as createCategoryRow,
  createCity as createCityRow,
  createOfferingFeature as createFeatureRow,
  createOffering as createOfferingRow,
  createOfferingQuoteOption as createQuoteOptionRow,
  createOfferingVariant as createVariantRow,
  deleteOfferingCategory as deleteCategoryRow,
  deleteCity as deleteCityRow,
  deleteOfferingFeature as deleteFeatureRow,
  deleteOffering as deleteOfferingRow,
  deleteOfferingQuoteOption as deleteQuoteOptionRow,
  deleteOfferingVariant as deleteVariantRow,
  getCategoryById,
  getOfferingById,
  listCities,
  listOfferingCategories,
  listOfferingCities,
  listOfferingFeatures,
  listOfferingQuoteOptions,
  listOfferings,
  listOfferingVariantFeatures,
  listOfferingVariants,
  type OfferingUse,
  readOfferingUse,
  setOfferingCities as setOfferingCitiesRow,
  updateOfferingCategory as updateCategoryRow,
  updateCity as updateCityRow,
  updateOfferingFeature as updateFeatureRow,
  updateOffering as updateOfferingRow,
  updateOfferingQuoteOption as updateQuoteOptionRow,
  updateOfferingVariant as updateVariantRow,
} from '@repo/db/queries/offering'
import { dailyFromTermPrice, termOfSlug, termPriceMinor } from '@repo/db/rental-terms'
import type { Offering as OfferingRow } from '@repo/db/schema/offerings'

import { assertPermission, type CoreContext } from '../context.ts'
import type { CoreDeps } from '../deps.ts'
import { type CoreError, dbErr, writeErr } from '../errors.ts'
import { type SupersededBlob, toImageRef, toSupersededBlob, UploadedImageInput } from '../media.ts'
import { err, isErr, ok, type Result } from '../result.ts'
import {
  type City,
  type FeatureGrant,
  type Offering,
  type OfferingCategory,
  type OfferingDetail,
  type OfferingFeature,
  type OfferingVariant,
  type QuoteOption,
  toCity,
  toOffering,
  toOfferingCategory,
  toOfferingFeature,
  toOfferingVariant,
  toQuoteOption,
} from './mappers.ts'
import {
  CreateCategoryInput,
  CreateCityInput,
  CreateFeatureInput,
  CreateOfferingInput,
  CreateQuoteOptionInput,
  CreateVariantInput,
  SetOfferingCitiesInput,
  UpdateCategoryInput,
  UpdateCityInput,
  UpdateFeatureInput,
  UpdateOfferingInput,
  UpdateQuoteOptionInput,
  UpdateVariantInput,
} from './schemas.ts'

/**
 * What one rung's edit actually did to a hire's ladder.
 *
 * `dailyNetMinor` is the price of ONE day that the typed rung implies, and is
 * `null` when nothing was spread — either the level is not a rung or the panel
 * asked for the single row. The panel says it out loud, because „149 zł za
 * dobę" is the sentence that explains why four other numbers just moved.
 *
 * `changed` lists only the rows whose price actually differs from what was
 * stored. A recompute that lands on the same figures is a save that says „bez
 * zmian" rather than one that claims five writes.
 */
export interface RentalLadderWrite {
  readonly dailyNetMinor: number | null
  readonly changed: readonly {
    readonly variantId: string
    readonly name: string
    readonly priceNetMinor: number
  }[]
}

/**
 * Catalogue use-cases: the guarded, framework-agnostic layer the panel writes
 * through and the storefront can read through. Reads require `offer:read`;
 * writes require `offer:create` / `offer:update` / `offer:delete`. Every write
 * calls {@link assertPermission} BEFORE touching `deps.db`.
 *
 * **One service where there were two.** `offer` and `production` were separate
 * only because their tables were; a camera, a room, an operator and a montage
 * package now take the same eleven use-cases, and adding a sixth kind of offer
 * adds none.
 *
 * The raw readers in `@repo/db/queries/offering` stay unguarded for public
 * pages; anything mutating, and anything the panel does, comes through here so
 * the permission travels with the use-case.
 */
export function createOfferingService(deps: CoreDeps) {
  /**
   * Rows → the full read model — comparison matrix, quote options, coverage and
   * cover pictures included — in six queries for the WHOLE catalogue rather
   * than six per offering.
   *
   * A cell with `isIncluded` false is dropped here rather than carried: the
   * read model says "these variants grant the row", and two ways to spell "no"
   * would be two ways for a renderer to disagree about a dimmed line.
   */
  async function withDetail(rows: OfferingRow[]): Promise<Result<OfferingDetail[], CoreError>> {
    const [variants, features, cells, options, coverage, covers] = await Promise.all([
      listOfferingVariants(deps.db),
      listOfferingFeatures(deps.db),
      listOfferingVariantFeatures(deps.db),
      listOfferingQuoteOptions(deps.db),
      listOfferingCities(deps.db),
      listEntityImages(
        deps.db,
        'OFFERING',
        COVER_ROLE,
        rows.map((row) => row.id),
      ),
    ])
    if (isErr(variants)) return err(dbErr(variants.error.message, variants.error.cause))
    if (isErr(features)) return err(dbErr(features.error.message, features.error.cause))
    if (isErr(cells)) return err(dbErr(cells.error.message, cells.error.cause))
    if (isErr(options)) return err(dbErr(options.error.message, options.error.cause))
    if (isErr(coverage)) return err(dbErr(coverage.error.message, coverage.error.cause))
    if (isErr(covers)) return err(dbErr(covers.error.message, covers.error.cause))

    const grantsByFeature = new Map<string, FeatureGrant[]>()
    for (const cell of cells.value) {
      if (!cell.isIncluded) continue
      const list = grantsByFeature.get(cell.offeringFeatureId) ?? []
      list.push({ variantId: cell.offeringVariantId, valueLabel: cell.valueLabel })
      grantsByFeature.set(cell.offeringFeatureId, list)
    }

    const featuresByOffering = new Map<string, OfferingFeature[]>()
    for (const row of features.value) {
      const list = featuresByOffering.get(row.offeringId) ?? []
      list.push(toOfferingFeature(row, grantsByFeature.get(row.id) ?? []))
      featuresByOffering.set(row.offeringId, list)
    }

    const variantsByOffering = new Map<string, (typeof variants.value)[number][]>()
    for (const variant of variants.value) {
      const list = variantsByOffering.get(variant.offeringId) ?? []
      list.push(variant)
      variantsByOffering.set(variant.offeringId, list)
    }

    const optionsByOffering = new Map<string, QuoteOption[]>()
    for (const row of options.value) {
      const list = optionsByOffering.get(row.offeringId) ?? []
      list.push(toQuoteOption(row))
      optionsByOffering.set(row.offeringId, list)
    }

    const citiesByOffering = new Map<string, string[]>()
    for (const link of coverage.value) {
      const list = citiesByOffering.get(link.offeringId) ?? []
      list.push(link.cityId)
      citiesByOffering.set(link.offeringId, list)
    }

    return ok(
      rows.map((row) => {
        const cover = covers.value.get(row.id)
        return {
          ...toOffering(
            row,
            variantsByOffering.get(row.id) ?? [],
            cover ? toImageRef(cover) : null,
          ),
          features: featuresByOffering.get(row.id) ?? [],
          quoteOptions: optionsByOffering.get(row.id) ?? [],
          cityIds: citiesByOffering.get(row.id) ?? [],
        }
      }),
    )
  }

  return {
    /** Every page of the offer and the groups inside them, ordered. */
    async listCategories(ctx: CoreContext): Promise<Result<OfferingCategory[], CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const rows = await listOfferingCategories(deps.db)
      if (isErr(rows)) return err(dbErr(rows.error.message, rows.error.cause))
      return ok(rows.value.map(toOfferingCategory))
    },

    /**
     * The whole catalogue with variants and covers — the panel's one list, which
     * it then filters by category in the browser.
     *
     * Hidden offerings and hidden variants come back too: the panel is where a
     * hidden thing gets un-hidden, and a list that omits them cannot do that.
     */
    async listOfferings(ctx: CoreContext): Promise<Result<Offering[], CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm

      const rows = await listOfferings(deps.db)
      if (isErr(rows)) return err(dbErr(rows.error.message, rows.error.cause))
      const variants = await listOfferingVariants(deps.db)
      if (isErr(variants)) return err(dbErr(variants.error.message, variants.error.cause))
      const covers = await listEntityImages(
        deps.db,
        'OFFERING',
        COVER_ROLE,
        rows.value.map((row) => row.id),
      )
      if (isErr(covers)) return err(dbErr(covers.error.message, covers.error.cause))

      const byOffering = new Map<string, (typeof variants.value)[number][]>()
      for (const variant of variants.value) {
        const list = byOffering.get(variant.offeringId) ?? []
        list.push(variant)
        byOffering.set(variant.offeringId, list)
      }
      return ok(
        rows.value.map((row) => {
          const cover = covers.value.get(row.id)
          return toOffering(row, byOffering.get(row.id) ?? [], cover ? toImageRef(cover) : null)
        }),
      )
    },

    /** Everything in one category, matrix included — what a CMS screen loads. */
    async listCategoryDetails(
      ctx: CoreContext,
      categoryId: string,
    ): Promise<Result<OfferingDetail[], CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const rows = await listOfferings(deps.db)
      if (isErr(rows)) return err(dbErr(rows.error.message, rows.error.cause))
      return withDetail(rows.value.filter((row) => row.categoryId === categoryId))
    },

    /** One offering with everything attached — what the edit screen loads. */
    async getOfferingDetail(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<OfferingDetail, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const row = await getOfferingById(deps.db, id)
      if (isErr(row)) return err(dbErr(row.error.message, row.error.cause))
      if (!row.value) return err({ kind: 'not_found', entity: 'offering', id })

      const detail = await withDetail([row.value])
      if (isErr(detail)) return detail
      const one = detail.value[0]
      if (!one) return err({ kind: 'not_found', entity: 'offering', id })
      return ok(one)
    },

    /**
     * Every offering→city link, flat. The panel needs "how many offerings
     * travel to Kraków?" for its delete guard, and asking per city would be a
     * query per row of a table that fits in one.
     */
    async listCoverage(
      ctx: CoreContext,
    ): Promise<Result<{ offeringId: string; cityId: string }[], CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const rows = await listOfferingCities(deps.db)
      if (isErr(rows)) return err(dbErr(rows.error.message, rows.error.cause))
      return ok(rows.value.map(({ offeringId, cityId }) => ({ offeringId, cityId })))
    },

    async listCities(ctx: CoreContext): Promise<Result<City[], CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const rows = await listCities(deps.db)
      if (isErr(rows)) return err(dbErr(rows.error.message, rows.error.cause))
      return ok(rows.value.map(toCity))
    },

    // ── writes (guarded before deps.db) ─────────────────────────────────────

    /**
     * Add something to the offer. It starts with no variants, which is a draft
     * rather than a mistake — the screen that created it is where the first
     * price goes, and a public page skips an offering with no priced variant.
     */
    async createOffering(ctx: CoreContext, input: unknown): Promise<Result<Offering, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'create')
      if (isErr(perm)) return perm
      const parsed = CreateOfferingInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const created = await createOfferingRow(
        deps.db,
        parsed.data as Parameters<typeof createOfferingRow>[1],
      )
      if (isErr(created)) return err(writeErr('offering', created.error))
      return ok(toOffering(created.value, []))
    },

    async updateOffering(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<Offering, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UpdateOfferingInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      if ('stockQuantity' in parsed.data) {
        const room = await stockLeavesRoom(deps, id, parsed.data.stockQuantity ?? null)
        if (isErr(room)) return room
      }

      const updated = await updateOfferingRow(
        deps.db,
        id,
        parsed.data as Parameters<typeof updateOfferingRow>[2],
      )
      if (isErr(updated)) return err(writeErr('offering', updated.error))
      if (!updated.value) return err({ kind: 'not_found', entity: 'offering', id })
      return ok(toOffering(updated.value, []))
    },

    /**
     * What would go with this row — for the sentence above „Usuń pozycję".
     *
     * `offer:read`, the same key the editor itself needs: it is a count of
     * things the caller can already see on their own screens, and a narrower
     * gate would mean the warning disappears for exactly the person the button
     * is shown to.
     */
    async offeringUse(ctx: CoreContext, id: string): Promise<Result<OfferingUse, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const use = await readOfferingUse(deps.db, id)
      if (isErr(use)) return err(dbErr(use.error.message, use.error.cause))
      return ok(use.value)
    },

    /**
     * Erase an offering, refused while a booking still points at it. The
     * `blockedBy` count is what turns a foreign-key violation into a sentence
     * the admin can act on.
     */
    async deleteOffering(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<{ deleted: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'delete')
      if (isErr(perm)) return perm

      const outcome = await deleteOfferingRow(deps.db, id)
      if (isErr(outcome)) return err(dbErr(outcome.error.message, outcome.error.cause))
      if (!outcome.value.deleted) {
        return err({
          kind: 'conflict',
          message: `Nie można usunąć — wskazuje na to ${outcome.value.blockedBy} rezerwacji.`,
        })
      }
      return ok({ deleted: true })
    },

    // ── variants ───────────────────────────────────────────────────────────

    async createVariant(
      ctx: CoreContext,
      input: unknown,
    ): Promise<Result<OfferingVariant, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'create')
      if (isErr(perm)) return perm
      const parsed = CreateVariantInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const created = await createVariantRow(
        deps.db,
        parsed.data as Parameters<typeof createVariantRow>[1],
      )
      if (isErr(created)) return err(writeErr('offering_variant', created.error))
      return ok(toOfferingVariant(created.value))
    },

    async updateVariant(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<OfferingVariant, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UpdateVariantInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const updated = await updateVariantRow(
        deps.db,
        id,
        parsed.data as Parameters<typeof updateVariantRow>[2],
      )
      if (isErr(updated)) return err(writeErr('offering_variant', updated.error))
      if (!updated.value) return err({ kind: 'not_found', entity: 'offering_variant', id })
      return ok(toOfferingVariant(updated.value))
    },

    /**
     * One rung of a hire's price ladder — and, unless told otherwise, the other four.
     *
     * ── WHY THE LADDER NEEDED A WRITE OF ITS OWN ─────────────────────────
     * Spec §11 sells a hire as five FIXED prices: doba, 3, 7, 14, 30 dób. They
     * were generated once, at seed time, from the daily rate through
     * `termPriceMinor` — and after that they were five unrelated numbers.
     * Editing „Doba" in the panel moved one of them and left the other four
     * quoting a ladder that no longer came off anything. On a screen whose
     * whole claim is „ta strona jest decydująca", that was the one place the
     * claim was false.
     *
     * So a rung now says what the DAY costs, and the rest follow:
     *
     *     daily = price × 10000 ÷ (days × (10000 − discountBps[days]))
     *
     * which is `termPriceMinor` read backwards. Typing into „Doba" is the
     * identity case; typing 402,30 into „3 doby" says a day is 149 zł and
     * prices the other four from that. The owner asked for exactly this:
     * „niezależnie czy zmienię 3, 7, 14 dobę reszta zmienia się automatycznie".
     *
     * ── AND WHY IT CAN BE TURNED OFF ─────────────────────────────────────
     * `spread: false` writes the one rung and nothing else — „muszę mieć
     * możliwość też zmienienia jednej opcji gdzie reszta zostaje". A promotion
     * on the thirty-day price is a real thing to want, and a rule with no
     * exception is a rule somebody edits the database around.
     *
     * Quote-only rungs are skipped rather than priced: the schema's
     * `offering_variant_price_quote_xor` refuses a price on one, and a level
     * sold „na zapytanie" is not a rung that lost its number.
     *
     * NOT a transaction, and that is a real limit: five updates, and a failure
     * halfway leaves a ladder half-recomputed. `Result` carries which rows
     * actually moved so the panel can say so; the alternative was a
     * transaction helper this query layer does not have.
     */
    async setRentalTermPrice(
      ctx: CoreContext,
      variantId: string,
      priceNetMinor: number,
      spread: boolean,
    ): Promise<Result<RentalLadderWrite, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      if (!Number.isInteger(priceNetMinor) || priceNetMinor <= 0) {
        return err({
          kind: 'conflict',
          message: 'Podaj cenę większą od zera.',
        })
      }

      const all = await listOfferingVariants(deps.db)
      if (isErr(all)) return err(dbErr(all.error.message, all.error.cause))
      const target = all.value.find((row) => row.id === variantId)
      if (!target) return err({ kind: 'not_found', entity: 'offering_variant', id: variantId })

      const days = termOfSlug(target.slug)
      // Not a rung at all, or the spread switched off: this is an ordinary
      // price edit and behaves exactly like one.
      const alone = days === null || !spread

      const dailyNetMinor = alone ? null : dailyFromTermPrice(priceNetMinor, days)

      const targets =
        alone || dailyNetMinor === null
          ? [{ row: target, next: priceNetMinor }]
          : all.value.flatMap((row) => {
              if (row.offeringId !== target.offeringId || row.isQuoteOnly) return []
              const rung = termOfSlug(row.slug)
              if (rung === null) return []
              const next =
                row.id === target.id ? priceNetMinor : termPriceMinor(dailyNetMinor, rung)
              return next > 0 ? [{ row, next }] : []
            })

      const changed: { variantId: string; name: string; priceNetMinor: number }[] = []
      for (const { row, next } of targets) {
        if (row.priceNetMinor === next) continue
        const written = await updateVariantRow(deps.db, row.id, { priceNetMinor: next })
        if (isErr(written)) return err(writeErr('offering_variant', written.error))
        if (!written.value) {
          return err({ kind: 'not_found', entity: 'offering_variant', id: row.id })
        }
        changed.push({ variantId: row.id, name: row.name, priceNetMinor: next })
      }

      return ok({ dailyNetMinor, changed })
    },

    async deleteVariant(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<{ deleted: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'delete')
      if (isErr(perm)) return perm
      const deleted = await deleteVariantRow(deps.db, id)
      if (isErr(deleted)) return err(dbErr(deleted.error.message, deleted.error.cause))
      if (!deleted.value) return err({ kind: 'not_found', entity: 'offering_variant', id })
      return ok({ deleted: true })
    },

    // ── comparison matrix ──────────────────────────────────────────────────

    /**
     * A comparison row and its cells, written together — see the query. The
     * label and the cells are one edit because a row saved without them would
     * advertise a feature no variant grants.
     */
    async createFeature(
      ctx: CoreContext,
      input: unknown,
    ): Promise<Result<OfferingFeature, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'create')
      if (isErr(perm)) return perm
      const parsed = CreateFeatureInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const { grants, ...values } = parsed.data
      const created = await createFeatureRow(deps.db, values, grants)
      if (isErr(created)) return err(writeErr('offering_feature', created.error))
      return ok(toOfferingFeature(created.value, grants))
    },

    async updateFeature(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<OfferingFeature, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UpdateFeatureInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const { grants, ...patch } = parsed.data
      const updated = await updateFeatureRow(
        deps.db,
        id,
        patch as Parameters<typeof updateFeatureRow>[2],
        grants ?? null,
      )
      if (isErr(updated)) return err(writeErr('offering_feature', updated.error))
      if (!updated.value) return err({ kind: 'not_found', entity: 'offering_feature', id })
      return ok(toOfferingFeature(updated.value, grants ?? []))
    },

    async deleteFeature(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<{ deleted: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'delete')
      if (isErr(perm)) return perm
      const deleted = await deleteFeatureRow(deps.db, id)
      if (isErr(deleted)) return err(dbErr(deleted.error.message, deleted.error.cause))
      if (!deleted.value) return err({ kind: 'not_found', entity: 'offering_feature', id })
      return ok({ deleted: true })
    },

    // ── quote options ──────────────────────────────────────────────────────

    async createQuoteOption(
      ctx: CoreContext,
      input: unknown,
    ): Promise<Result<QuoteOption, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'create')
      if (isErr(perm)) return perm
      const parsed = CreateQuoteOptionInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const created = await createQuoteOptionRow(
        deps.db,
        parsed.data as Parameters<typeof createQuoteOptionRow>[1],
      )
      if (isErr(created)) return err(writeErr('offering_quote_option', created.error))
      return ok(toQuoteOption(created.value))
    },

    async updateQuoteOption(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<QuoteOption, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UpdateQuoteOptionInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const updated = await updateQuoteOptionRow(
        deps.db,
        id,
        parsed.data as Parameters<typeof updateQuoteOptionRow>[2],
      )
      if (isErr(updated)) return err(writeErr('offering_quote_option', updated.error))
      if (!updated.value) return err({ kind: 'not_found', entity: 'offering_quote_option', id })
      return ok(toQuoteOption(updated.value))
    },

    async deleteQuoteOption(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<{ deleted: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'delete')
      if (isErr(perm)) return perm
      const deleted = await deleteQuoteOptionRow(deps.db, id)
      if (isErr(deleted)) return err(dbErr(deleted.error.message, deleted.error.cause))
      if (!deleted.value) return err({ kind: 'not_found', entity: 'offering_quote_option', id })
      return ok({ deleted: true })
    },

    // ── categories ─────────────────────────────────────────────────────────

    async createCategory(
      ctx: CoreContext,
      input: unknown,
    ): Promise<Result<OfferingCategory, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'create')
      if (isErr(perm)) return perm
      const parsed = CreateCategoryInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const created = await createCategoryRow(
        deps.db,
        parsed.data as Parameters<typeof createCategoryRow>[1],
      )
      if (isErr(created)) return err(writeErr('offering_category', created.error))
      return ok(toOfferingCategory(created.value))
    },

    async updateCategory(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<OfferingCategory, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UpdateCategoryInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const updated = await updateCategoryRow(
        deps.db,
        id,
        parsed.data as Parameters<typeof updateCategoryRow>[2],
      )
      if (isErr(updated)) return err(writeErr('offering_category', updated.error))
      if (!updated.value) return err({ kind: 'not_found', entity: 'offering_category', id })
      return ok(toOfferingCategory(updated.value))
    },

    async deleteCategory(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<{ deleted: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'delete')
      if (isErr(perm)) return perm
      const outcome = await deleteCategoryRow(deps.db, id)
      if (isErr(outcome)) return err(dbErr(outcome.error.message, outcome.error.cause))
      if (!outcome.value.deleted) {
        return err({
          kind: 'conflict',
          message: `Nie można usunąć — kategoria trzyma jeszcze ${outcome.value.blockedBy} pozycji.`,
        })
      }
      return ok({ deleted: true })
    },

    // ── cities and coverage ────────────────────────────────────────────────

    async createCity(ctx: CoreContext, input: unknown): Promise<Result<City, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'create')
      if (isErr(perm)) return perm
      const parsed = CreateCityInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const created = await createCityRow(deps.db, parsed.data)
      if (isErr(created)) return err(writeErr('city', created.error))
      return ok(toCity(created.value))
    },

    async updateCity(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<City, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UpdateCityInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const updated = await updateCityRow(
        deps.db,
        id,
        parsed.data as Parameters<typeof updateCityRow>[2],
      )
      if (isErr(updated)) return err(writeErr('city', updated.error))
      if (!updated.value) return err({ kind: 'not_found', entity: 'city', id })
      return ok(toCity(updated.value))
    },

    async deleteCity(ctx: CoreContext, id: string): Promise<Result<{ deleted: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'delete')
      if (isErr(perm)) return perm
      const outcome = await deleteCityRow(deps.db, id)
      if (isErr(outcome)) return err(dbErr(outcome.error.message, outcome.error.cause))
      if (!outcome.value.deleted) {
        return err({
          kind: 'conflict',
          message: `Nie można usunąć — wskazuje na to ${outcome.value.blockedBy} rezerwacji.`,
        })
      }
      return ok({ deleted: true })
    },

    /** Replace an offering's whole coverage list. Empty is a legal answer. */
    async setOfferingCities(
      ctx: CoreContext,
      input: unknown,
    ): Promise<Result<{ ok: true }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = SetOfferingCitiesInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const written = await setOfferingCitiesRow(
        deps.db,
        parsed.data.offeringId,
        parsed.data.cityIds,
      )
      if (isErr(written)) return err(writeErr('offering_city', written.error))
      return ok({ ok: true })
    },

    // ── pictures ───────────────────────────────────────────────────────────

    /**
     * Point an offering's cover at a blob this app just uploaded, replacing any
     * previous one. Same contract as `users.setOwnAvatar`: the bytes are already
     * in the store, so a failure here leaves an orphan the caller deletes, and
     * the superseded blobs come back to be deleted on success.
     */
    async setOfferingImage(
      ctx: CoreContext,
      id: string,
      input: unknown,
    ): Promise<Result<{ superseded: SupersededBlob[] }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm
      const parsed = UploadedImageInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      // `asset_link.entity_id` has no foreign key, so an unknown id would link a
      // picture to nothing at all — the row is checked here instead.
      const row = await getOfferingById(deps.db, id)
      if (isErr(row)) return err(dbErr(row.error.message, row.error.cause))
      if (!row.value) return err({ kind: 'not_found', entity: 'offering', id })

      const written = await setEntityImage(
        deps.db,
        { entityType: 'OFFERING', entityId: id, role: COVER_ROLE },
        { ...parsed.data, uploadedByUserId: ctx.userId ?? null },
      )
      if (isErr(written)) return err(dbErr(written.error.message, written.error.cause))
      return ok({ superseded: written.value.superseded.map(toSupersededBlob) })
    },

    /** Drop an offering's cover. Idempotent — no picture is not an error. */
    async removeOfferingImage(
      ctx: CoreContext,
      id: string,
    ): Promise<Result<{ superseded: SupersededBlob[] }, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'update')
      if (isErr(perm)) return perm

      const cleared = await clearEntityImage(deps.db, {
        entityType: 'OFFERING',
        entityId: id,
        role: COVER_ROLE,
      })
      if (isErr(cleared)) return err(dbErr(cleared.error.message, cleared.error.cause))
      return ok({ superseded: cleared.value.superseded.map(toSupersededBlob) })
    },

    /** One category by id — the edit screen's own load. */
    async getCategory(ctx: CoreContext, id: string): Promise<Result<OfferingCategory, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'read')
      if (isErr(perm)) return perm
      const row = await getCategoryById(deps.db, id)
      if (isErr(row)) return err(dbErr(row.error.message, row.error.cause))
      if (!row.value) return err({ kind: 'not_found', entity: 'offering_category', id })
      return ok(toOfferingCategory(row.value))
    },
  }
}

export type OfferingService = ReturnType<typeof createOfferingService>

/**
 * Refuse a stock edit that would leave the shelf short of what is already out.
 *
 * The equipment and studio half of spec §6's rule — "sprzedane sloty są
 * zablokowane" — which §10 and §11 restate for rooms and gear. Thumbnails have
 * `capacity_day_sold_within_capacity` to lean on because the sold count sits in
 * the same row as the capacity; a rental's holds live in `booking`, so no CHECK
 * can see both sides and the service has to.
 *
 * PEAK, not total: ten hires of one camera across ten weekends hold one unit at
 * a time, and summing them would freeze the number after a busy quarter.
 *
 * The comparison is on `effectiveStock`, not on the raw column, because NULL is
 * not always "more room". For a `DATE_RANGE` hire NULL is read as ONE — there is
 * no such thing as an unlimited number of one camera body — so clearing the
 * field on an offering with three units out is a REDUCTION, and reading it as
 * "unlimited, therefore safe" would wave through exactly the edit this exists
 * to catch.
 */
async function stockLeavesRoom(
  deps: CoreDeps,
  offeringId: string,
  nextStock: number | null,
): Promise<Result<void, CoreError>> {
  const found = await getOfferingById(deps.db, offeringId)
  if (isErr(found)) return err(dbErr(found.error.message, found.error.cause))
  if (!found.value) return err({ kind: 'not_found', entity: 'offering', id: offeringId })

  // Nothing on this offering can be reserved, so there is no shelf to run short
  // of. Per-piece work carries a stock number only as catalogue copy.
  if (found.value.bookingMode === 'NONE') return ok(undefined)

  const ceiling = effectiveStock(found.value.bookingMode, nextStock)
  if (ceiling === null) return ok(undefined)

  const holds = await listFutureHolds(deps.db, offeringId, deps.clock.now())
  if (isErr(holds)) return err(dbErr(holds.error.message, holds.error.cause))

  const peak = peakHeldQuantity(holds.value)
  if (peak <= ceiling) return ok(undefined)

  return err({
    kind: 'conflict',
    message:
      `W kalendarzu jest już ${peak} szt. zarezerwowanych jednocześnie, ` +
      `więc nie zejdziesz poniżej ${peak}. Odwołaj rezerwację albo wpisz co najmniej ${peak}.`,
  })
}
